import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getOrganogram } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Building, Users, ChevronDown, ChevronRight } from 'lucide-react';

import { useDepartments } from '../hooks/useDepartments';

const getRoleBg = (role) => {
  const map = {
    'Super Admin': 'border-purple-400 bg-purple-50 dark:bg-purple-950/40',
    'Admin': 'border-blue-400 bg-blue-50 dark:bg-blue-950/40',
    'Coordinator': 'border-emerald-400 bg-emerald-50 dark:bg-emerald-950/40',
  };
  return map[role] || 'border-slate-300 bg-white dark:bg-slate-900';
};

const getRoleBadge = (role) => {
  const map = {
    'Super Admin': 'bg-purple-500/15 text-purple-600 dark:text-purple-400 border border-purple-500/20',
    'Admin': 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20',
    'Coordinator': 'bg-green-500/15 text-green-600 dark:text-green-400 border border-green-500/20',
  };
  return map[role] || 'bg-gray-500/15 text-gray-600 dark:text-gray-400 border border-gray-500/20';
};

// Flatten tree into an array with depth info (iterative, no recursion)
function flattenTree(nodes) {
  const result = [];
  const stack = [];
  // Push root nodes in reverse so first appears first
  for (let i = nodes.length - 1; i >= 0; i--) {
    stack.push({ node: nodes[i], depth: 0, parentId: null });
  }
  while (stack.length > 0) {
    const item = stack.pop();
    const hasChildren = item.node.children && item.node.children.length > 0;
    result.push({
      ...item.node,
      depth: item.depth,
      parentId: item.parentId,
      hasChildren,
    });
    if (hasChildren) {
      for (let i = item.node.children.length - 1; i >= 0; i--) {
        stack.push({
          node: item.node.children[i],
          depth: item.depth + 1,
          parentId: item.node.id,
        });
      }
    }
  }
  return result;
}

export const OrganogramPage = () => {
  const DEPARTMENTS = useDepartments();
  const { user, isSuperAdmin } = useAuth();
  const [organogram, setOrganogram] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState({});
  const [selectedDepartment, setSelectedDepartment] = useState(
    isSuperAdmin ? '' : user?.department || ''
  );

  const fetchOrganogram = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getOrganogram(selectedDepartment || undefined);
      setOrganogram(response.data || []);
    } catch (error) {
      console.error('Failed to fetch organogram:', error);
      setOrganogram([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDepartment]);

  useEffect(() => {
    fetchOrganogram();
  }, [fetchOrganogram]);

  const toggleCollapse = (id) => {
    setCollapsed(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Build flat list and filter out collapsed children
  const flatList = flattenTree(organogram);
  
  // Filter out children of collapsed nodes
  const collapsedSet = new Set();
  const visibleList = [];
  for (const item of flatList) {
    if (collapsedSet.has(item.parentId)) {
      collapsedSet.add(item.id);
      continue;
    }
    if (collapsed[item.parentId]) {
      collapsedSet.add(item.id);
      continue;
    }
    visibleList.push(item);
  }

  return (
    <div className="space-y-6" data-testid="organogram-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold">Org Chart</h1>
          <p className="text-muted-foreground mt-1">Organization hierarchy</p>
        </div>
        {isSuperAdmin && (
          <Select value={selectedDepartment || "all"} onValueChange={(v) => setSelectedDepartment(v === "all" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="department-filter">
              <Building className="h-4 w-4 mr-2" />
              <SelectValue placeholder="All Departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {DEPARTMENTS.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Card className="border-border/50">
        <CardContent className="p-6 overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5A623]"></div>
            </div>
          ) : visibleList.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No organization data found</p>
            </div>
          ) : (
            <div className="space-y-2 min-w-[400px]">
              {visibleList.map((item) => {
                const indent = item.depth * 32;
                return (
                  <div key={item.id} style={{ paddingLeft: `${indent}px` }} data-testid={`org-node-${item.id}`}>
                    {item.depth > 0 && (
                      <div className="flex items-center mb-1 ml-2">
                        <div className="w-5 border-t-2 border-slate-300 dark:border-slate-600" />
                      </div>
                    )}
                    <div
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl border-2 shadow-sm transition-all duration-200 hover:shadow-md ${getRoleBg(item.role)}`}
                    >
                      <Avatar className="h-9 w-9 bg-[#F5A623]/20 shrink-0">
                        <AvatarFallback className="text-[#F5A623] font-semibold text-sm">
                          {item.name ? item.name.charAt(0) : 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{item.email}</p>
                        {item.department && <p className="text-[10px] text-muted-foreground mt-0.5">{item.department}</p>}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={`text-[10px] px-1.5 py-0 ${getRoleBadge(item.role)}`}>{item.role}</Badge>
                        {item.hasChildren && (
                          <button
                            onClick={() => toggleCollapse(item.id)}
                            className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
                            data-testid={`toggle-${item.id}`}
                          >
                            {collapsed[item.id] ? (
                              <ChevronRight className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-border/50">
        <CardHeader>
          <CardTitle className="text-lg">Role Legend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            {[
              { role: 'Super Admin', desc: 'Full system access' },
              { role: 'Admin', desc: 'Department head' },
              { role: 'Coordinator', desc: 'Team lead' },
              { role: 'Member', desc: 'Team member' },
            ].map((entry) => (
              <div key={entry.role} className="flex items-center gap-2">
                <Badge className={getRoleBadge(entry.role)}>{entry.role}</Badge>
                <span className="text-sm text-muted-foreground">{entry.desc}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
